import ELK from 'elkjs/lib/elk.bundled.js';
import type { ElkNode, LayoutOptions } from 'elkjs/lib/elk-api';
import { ArchGraph, SHARED_GROUP_ID } from './graph';

export const NODE_WIDTH = 220;
export const NODE_HEIGHT = 52;
export const GROUP_HEADER = 36;

export type Positioned = { x: number; y: number };
export type PositionedGroup = Positioned & { width: number; height: number };

export type Layout = {
  nodes: Map<string, Positioned>; // координаты относительно своей группы
  absolute: Map<string, Positioned>; // центры узлов в координатах холста
  groups: Map<string, PositionedGroup>;
  lanes: Map<string, number>; // x левого края слоя узла в координатах холста: там идут вертикали связей
};

const elk = new ELK();

const PAD_X = 20;
const PAD_TOP = GROUP_HEADER + 12;
const PAD_BOTTOM = 20;
const GROUP_GAP = 56; // между системами в ряду
const ROW_GAP = 64; // между рядом систем и полосой общих ресурсов
const BAND_NODE_GAP = 16;
const BAND_ROW_GAP = 20;
const NODE_GAP = 18;
export const LAYER_GAP = 56;
const MAX_LAYER_NODES = 8; // выше — слой раскладывается в две колонки
const COLUMN_GAP = 24;
const PITCH = NODE_HEIGHT + NODE_GAP;

type Inner = {
  width: number;
  height: number;
  positions: Map<string, Positioned>;
  lanes: Map<string, number>;
};

// ELK ставит все листья системы (ресурсы, API) в последний слой, и у ВКС он вытягивается в столбец
// из 16 узлов. Высокие слои раскладываются в две колонки «кирпичом»: вторая сдвинута на полшага,
// поэтому горизонталь к узлу второй колонки проходит в промежутке между узлами первой.
// Остальные слои ELK растянул под высокий столбец — их высота сжимается в той же пропорции.
function splitTallLayers(positions: Map<string, Positioned>): Inner {
  const layers = new Map<number, string[]>();
  for (const [id, pos] of positions) {
    const x = Math.round(pos.x);
    layers.set(x, [...(layers.get(x) ?? []), id]);
  }
  const allYs = [...positions.values()].map(p => p.y);
  const oldTop = Math.min(...allYs);
  const oldSpan = Math.max(...allYs) - oldTop;
  const tallest = Math.max(...[...layers.values()].map(ids => ids.length));
  const newSpan =
    tallest > MAX_LAYER_NODES
      ? (Math.ceil(tallest / 2) - 1) * PITCH + PITCH / 2
      : oldSpan;
  const newTop = oldTop + (oldSpan - newSpan) / 2;
  const scale = oldSpan > 0 ? newSpan / oldSpan : 1;
  const result = new Map<string, Positioned>();
  const lanes = new Map<string, number>();
  let shift = 0;
  for (const [x, ids] of [...layers.entries()].sort((a, b) => a[0] - b[0])) {
    const left = x + shift;
    ids.sort((a, b) => positions.get(a)!.y - positions.get(b)!.y);
    if (ids.length > MAX_LAYER_NODES) {
      const height = (Math.ceil(ids.length / 2) - 1) * PITCH + PITCH / 2;
      const top = newTop + (newSpan - height) / 2;
      ids.forEach((id, i) => {
        const column = i % 2;
        result.set(id, {
          x: left + column * (NODE_WIDTH + COLUMN_GAP),
          y: top + Math.floor(i / 2) * PITCH + column * (PITCH / 2),
        });
      });
      shift += NODE_WIDTH + COLUMN_GAP;
    } else {
      // Сжатие с сохранением порядка; перекрытия раздвигаются вниз.
      let prev = -Infinity;
      for (const id of ids) {
        const y = Math.max(newTop + (positions.get(id)!.y - oldTop) * scale, prev + PITCH);
        result.set(id, { x: left, y });
        prev = y;
      }
    }
    for (const id of ids) lanes.set(id, left);
  }
  const minY = Math.min(...[...result.values()].map(p => p.y));
  let width = 0;
  let height = 0;
  for (const pos of result.values()) {
    pos.y += PAD_TOP - minY;
    width = Math.max(width, pos.x + NODE_WIDTH + PAD_X);
    height = Math.max(height, pos.y + NODE_HEIGHT + PAD_BOTTOM);
  }
  return { width, height, positions: result, lanes };
}

// Раскладка узлов внутри одной системы: слева направо по её внутренним связям, либо сеткой,
// если внутренних связей нет.
async function layoutSystem(graph: ArchGraph, groupId: string): Promise<Inner> {
  const ids = new Set(graph.nodes.filter(n => n.groupId === groupId).map(n => n.id));
  const edges = graph.edges.filter(e => ids.has(e.source) && ids.has(e.target));
  const padding = `[top=${PAD_TOP},left=${PAD_X},bottom=${PAD_BOTTOM},right=${PAD_X}]`;
  const layoutOptions: LayoutOptions = edges.length
    ? {
        'elk.algorithm': 'layered',
        'elk.direction': 'RIGHT',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.padding': padding,
        'elk.spacing.nodeNode': String(NODE_GAP),
        'elk.layered.spacing.nodeNodeBetweenLayers': String(LAYER_GAP),
        'elk.aspectRatio': '1.4',
        'elk.spacing.componentComponent': '24',
      }
    : {
        'elk.algorithm': 'rectpacking',
        'elk.padding': padding,
        'elk.spacing.nodeNode': '18',
        'elk.aspectRatio': '1.6',
      };
  const root: ElkNode = {
    id: groupId,
    layoutOptions,
    children: [...ids].map(id => ({ id, width: NODE_WIDTH, height: NODE_HEIGHT })),
    edges: edges.map(e => ({ id: e.id, sources: [e.source], targets: [e.target] })),
  };
  const result = await elk.layout(root);
  const positions = new Map<string, Positioned>();
  for (const child of result.children ?? []) {
    positions.set(child.id, { x: child.x ?? 0, y: child.y ?? 0 });
  }
  if (edges.length) return splitTallLayers(positions);
  return {
    width: result.width ?? 0,
    height: result.height ?? 0,
    positions,
    lanes: new Map([...positions].map(([id, pos]) => [id, pos.x])),
  };
}

// Все перестановки массива (для 6–8 систем их немного, перебор занимает доли миллисекунды).
function* permutations<T>(items: T[]): Generator<T[]> {
  if (items.length <= 1) {
    yield items;
    return;
  }
  for (let i = 0; i < items.length; i++) {
    const rest = [...items.slice(0, i), ...items.slice(i + 1)];
    for (const perm of permutations(rest)) yield [items[i], ...perm];
  }
}

type Arrangement = { top: string[]; bottom: string[] };

// Раскладка «системы в два ряда, общие ресурсы полосой посередине». К общим ресурсам сходится
// больше всего связей, поэтому они стоят между рядами: связи идут короткими вертикалями.
// Ряды и порядок систем подбираются перебором: короче связи между системами, ровнее ширина рядов.
// Ресурсы в полосе ставятся по барицентру своих потребителей — под теми, кто ими пользуется.
export async function layoutGraph(graph: ArchGraph): Promise<Layout> {
  const systemIds = graph.groups.map(g => g.id).filter(id => id !== SHARED_GROUP_ID);
  const sharedIds = graph.nodes.filter(n => n.groupId === SHARED_GROUP_ID).map(n => n.id);
  const groupOf = new Map(graph.nodes.map(n => [n.id, n.groupId]));

  const inner = new Map<string, Inner>();
  await Promise.all(
    systemIds.map(async id => inner.set(id, await layoutSystem(graph, id))),
  );

  // Связность систем между собой (связи с общими ресурсами решает полоса).
  const pairWeight = new Map<string, number>();
  for (const edge of graph.edges) {
    const a = groupOf.get(edge.source);
    const b = groupOf.get(edge.target);
    if (!a || !b || a === b || a === SHARED_GROUP_ID || b === SHARED_GROUP_ID) continue;
    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
    pairWeight.set(key, (pairWeight.get(key) ?? 0) + 1);
  }

  const rowWidth = (row: string[]) =>
    row.reduce((w, id) => w + inner.get(id)!.width, 0) +
    Math.max(0, row.length - 1) * GROUP_GAP;

  const place = ({ top, bottom }: Arrangement, bandHeight: number) => {
    const topHeight = Math.max(0, ...top.map(id => inner.get(id)!.height));
    const width = Math.max(rowWidth(top), rowWidth(bottom));
    const boxes = new Map<string, PositionedGroup>();
    const placeRow = (row: string[], y: number, alignBottom: boolean, rowHeight: number) => {
      let x = (width - rowWidth(row)) / 2;
      for (const id of row) {
        const { width: w, height: h } = inner.get(id)!;
        // Верхний ряд прижат к полосе снизу, нижний — сверху: связи к ресурсам короче.
        boxes.set(id, { x, y: alignBottom ? y + rowHeight - h : y, width: w, height: h });
        x += w + GROUP_GAP;
      }
    };
    placeRow(top, 0, true, topHeight);
    const bandY = topHeight + (sharedIds.length ? ROW_GAP : 0);
    const bottomY = bandY + bandHeight + (sharedIds.length ? ROW_GAP : ROW_GAP / 2);
    placeRow(bottom, bottomY, false, 0);
    return { boxes, width, bandY };
  };

  const bandHeightEstimate = sharedIds.length
    ? PAD_TOP + 2 * NODE_HEIGHT + BAND_ROW_GAP + PAD_BOTTOM
    : 0;
  const cost = (arrangement: Arrangement) => {
    const { boxes } = place(arrangement, bandHeightEstimate);
    let total = 0;
    for (const [key, weight] of pairWeight) {
      const [a, b] = key.split('|');
      const ba = boxes.get(a)!;
      const bb = boxes.get(b)!;
      const dx = Math.abs(ba.x + ba.width / 2 - (bb.x + bb.width / 2));
      const dy = Math.abs(ba.y + ba.height / 2 - (bb.y + bb.height / 2));
      total += weight * (dx + dy);
    }
    total += 2 * Math.abs(rowWidth(arrangement.top) - rowWidth(arrangement.bottom));
    return total;
  };

  const split = Math.ceil(systemIds.length / 2);
  let best: Arrangement = { top: systemIds.slice(0, split), bottom: systemIds.slice(split) };
  if (systemIds.length > 1 && systemIds.length <= 8) {
    let bestCost = Infinity;
    for (const perm of permutations(systemIds)) {
      const candidate = { top: perm.slice(0, split), bottom: perm.slice(split) };
      const c = cost(candidate);
      if (c < bestCost) {
        bestCost = c;
        best = candidate;
      }
    }
  }

  // Соседи общих ресурсов среди узлов систем.
  const neighbours = new Map<string, string[]>();
  for (const edge of graph.edges) {
    const pairs: [string, string][] = [
      [edge.source, edge.target],
      [edge.target, edge.source],
    ];
    for (const [self, other] of pairs) {
      if (groupOf.get(self) !== SHARED_GROUP_ID || groupOf.get(other) === SHARED_GROUP_ID) continue;
      neighbours.set(self, [...(neighbours.get(self) ?? []), other]);
    }
  }

  // Строка полосы у верхнего ряда — для ресурсов, связанных в основном с ним, у нижнего — остальные.
  const topSet = new Set(best.top);
  const upper: string[] = [];
  const lower: string[] = [];
  for (const id of sharedIds) {
    const ns = neighbours.get(id) ?? [];
    const topCount = ns.filter(n => topSet.has(groupOf.get(n)!)).length;
    (ns.length && topCount * 2 >= ns.length ? upper : lower).push(id);
  }
  const bandRows = [upper, lower].filter(r => r.length);
  const bandHeight = sharedIds.length
    ? PAD_TOP + bandRows.length * NODE_HEIGHT + (bandRows.length - 1) * BAND_ROW_GAP + PAD_BOTTOM
    : 0;
  const { boxes, width, bandY } = place(best, bandHeight);

  const nodes = new Map<string, Positioned>();
  const absolute = new Map<string, Positioned>();
  const lanes = new Map<string, number>();
  for (const id of systemIds) {
    const box = boxes.get(id)!;
    for (const [nodeId, lane] of inner.get(id)!.lanes) lanes.set(nodeId, box.x + lane);
    for (const [nodeId, pos] of inner.get(id)!.positions) {
      nodes.set(nodeId, pos);
      absolute.set(nodeId, {
        x: box.x + pos.x + NODE_WIDTH / 2,
        y: box.y + pos.y + NODE_HEIGHT / 2,
      });
    }
  }

  if (sharedIds.length) {
    let minX = Infinity;
    let maxX = -Infinity;
    const bandPos = new Map<string, Positioned>();
    const target = (id: string) => {
      const xs = (neighbours.get(id) ?? [])
        .map(n => absolute.get(n)?.x)
        .filter((x): x is number => x !== undefined);
      return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : width / 2;
    };
    bandRows.forEach((row, rowIndex) => {
      const y = bandY + PAD_TOP + rowIndex * (NODE_HEIGHT + BAND_ROW_GAP);
      const ordered = [...row].sort((a, b) => target(a) - target(b));
      // Желаемые позиции по барицентру; перекрытия раздвигаются вправо, затем строка центрируется
      // относительно желаемых позиций.
      const desired = ordered.map(id => target(id) - NODE_WIDTH / 2);
      const xs = [...desired];
      for (let i = 1; i < xs.length; i++) {
        xs[i] = Math.max(xs[i], xs[i - 1] + NODE_WIDTH + BAND_NODE_GAP);
      }
      const shift =
        desired.reduce((a, b) => a + b, 0) / desired.length -
        xs.reduce((a, b) => a + b, 0) / xs.length;
      ordered.forEach((id, i) => {
        const x = xs[i] + shift;
        bandPos.set(id, { x, y });
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x + NODE_WIDTH);
      });
    });
    const bandBox: PositionedGroup = {
      x: minX - PAD_X,
      y: bandY,
      width: maxX - minX + 2 * PAD_X,
      height: bandHeight,
    };
    boxes.set(SHARED_GROUP_ID, bandBox);
    for (const [id, pos] of bandPos) {
      nodes.set(id, { x: pos.x - bandBox.x, y: pos.y - bandBox.y });
      absolute.set(id, { x: pos.x + NODE_WIDTH / 2, y: pos.y + NODE_HEIGHT / 2 });
    }
  }

  return { nodes, absolute, groups: boxes, lanes };
}
